import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Activity, AlertTriangle, CheckCircle, XCircle, Clock, RefreshCw, ArrowLeft } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { useAuth } from '../../contexts/AuthContext';
import { getSecondsUntilExpiration, getTokenLifetimePercentage, formatDuration } from '../../utils/jwtHelpers';

interface AuthMetric {
  metric_type: string;
  metric_value: number;
  metric_unit: string;
  time_window: string;
  created_at: string;
  metadata?: any;
}

interface AuthEvent {
  id: string;
  event_type: string;
  event_status: string;
  created_at: string;
  user_id?: string;
  metadata?: any;
}

interface AuthError {
  id: string;
  error_type: string;
  error_message: string;
  created_at: string;
  user_id?: string;
}

interface AuthAlert {
  id: string;
  alert_type: string;
  alert_severity: string;
  alert_message: string;
  sent_at: string;
  resolved_at?: string;
}

const AdminAuthHealth: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [metrics, setMetrics] = useState<AuthMetric[]>([]);
  const [recentEvents, setRecentEvents] = useState<AuthEvent[]>([]);
  const [recentErrors, setRecentErrors] = useState<AuthError[]>([]);
  const [unresolvedAlerts, setUnresolvedAlerts] = useState<AuthAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [tokenInfo, setTokenInfo] = useState<{
    secondsRemaining: number;
    percentageRemaining: number;
  } | null>(null);

  useEffect(() => {
    loadAuthHealth();
    const interval = setInterval(loadAuthHealth, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (session?.access_token) {
      updateTokenInfo();
      const interval = setInterval(updateTokenInfo, 1000); // Update every second
      return () => clearInterval(interval);
    }
  }, [session]);

  const updateTokenInfo = () => {
    if (!session?.access_token) return;

    setTokenInfo({
      secondsRemaining: getSecondsUntilExpiration(session.access_token),
      percentageRemaining: getTokenLifetimePercentage(session.access_token),
    });
  };

  const loadAuthHealth = async () => {
    try {
      setLoading(true);

      // Load metrics
      const { data: metricsData } = await supabase
        .from('auth_metrics')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (metricsData) {
        setMetrics(metricsData);
      }

      // Load recent events
      const { data: eventsData } = await supabase
        .from('auth_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (eventsData) {
        setRecentEvents(eventsData);
      }

      // Load recent errors
      const { data: errorsData } = await supabase
        .from('auth_errors')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (errorsData) {
        setRecentErrors(errorsData);
      }

      // Load unresolved alerts
      const { data: alertsData } = await supabase
        .from('auth_alerts')
        .select('*')
        .is('resolved_at', null)
        .order('sent_at', { ascending: false });

      if (alertsData) {
        setUnresolvedAlerts(alertsData);
      }
    } catch (error: any) {
      console.error('Error loading auth health:', error);
      toast.error('Failed to load auth health data');
    } finally {
      setLoading(false);
    }
  };

  const resolveAlert = async (alertId: string) => {
    try {
      const { error } = await supabase
        .from('auth_alerts')
        .update({ resolved_at: new Date().toISOString() })
        .eq('id', alertId);

      if (error) throw error;

      toast.success('Alert marked as resolved');
      loadAuthHealth();
    } catch (error: any) {
      console.error('Error resolving alert:', error);
      toast.error('Failed to resolve alert');
    }
  };

  const getMetricByType = (type: string, window: string): AuthMetric | undefined => {
    return metrics.find(m => m.metric_type === type && m.time_window === window);
  };

  const refreshSuccessRate5m = getMetricByType('token_refresh_success_rate', '5min');
  const errorRate5m = getMetricByType('auth_error_rate', '5min');
  const activeSessions = getMetricByType('active_sessions', '5min');
  const degradedModeEntries = getMetricByType('degraded_mode_entries', '5min');

  if (loading && metrics.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Authentication Health</h1>
            <p className="text-muted-foreground mt-1">
              Monitor JWT token lifecycle, auth errors, and system health
            </p>
          </div>
          <button
            onClick={loadAuthHealth}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
        </div>
      </div>

      {/* Current Session Token Info */}
      {tokenInfo && (
        <div className="bg-card rounded-lg border p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-primary" />
            <h2 className="text-lg font-semibold">Current Session Token</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-background rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Time Remaining</div>
              <div className="text-2xl font-bold">{formatDuration(tokenInfo.secondsRemaining)}</div>
            </div>

            <div className="bg-background rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Token Lifetime</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${tokenInfo.percentageRemaining}%` }}
                  />
                </div>
                <span className="text-sm font-semibold">{Math.round(tokenInfo.percentageRemaining)}%</span>
              </div>
            </div>

            <div className="bg-background rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Status</div>
              <div className="flex items-center gap-2">
                {tokenInfo.percentageRemaining > 25 ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-green-600 dark:text-green-400 font-medium">Healthy</span>
                  </>
                ) : tokenInfo.percentageRemaining > 10 ? (
                  <>
                    <Clock className="h-5 w-5 text-yellow-500" />
                    <span className="text-yellow-600 dark:text-yellow-400 font-medium">Refreshing Soon</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    <span className="text-red-600 dark:text-red-400 font-medium">Expiring Soon</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unresolved Alerts */}
      {unresolvedAlerts.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <h2 className="text-lg font-semibold text-red-600">Unresolved Alerts ({unresolvedAlerts.length})</h2>
          </div>

          <div className="space-y-3">
            {unresolvedAlerts.map((alert) => (
              <div key={alert.id} className="bg-background rounded-lg p-4 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      alert.alert_severity === 'critical' ? 'bg-red-500 text-white' :
                      alert.alert_severity === 'warning' ? 'bg-yellow-500 text-white' :
                      'bg-blue-500 text-white'
                    }`}>
                      {alert.alert_severity.toUpperCase()}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {new Date(alert.sent_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="font-medium">{alert.alert_message}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Type: {alert.alert_type.replace(/_/g, ' ')}
                  </p>
                </div>
                <button
                  onClick={() => resolveAlert(alert.id)}
                  className="ml-4 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                >
                  Resolve
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border p-6">
          <div className="flex items-center justify-between mb-2">
            <Activity className="h-5 w-5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Last 5 min</span>
          </div>
          <div className="text-2xl font-bold">
            {refreshSuccessRate5m ? `${refreshSuccessRate5m.metric_value}%` : 'N/A'}
          </div>
          <div className="text-sm text-muted-foreground">Token Refresh Rate</div>
        </div>

        <div className="bg-card rounded-lg border p-6">
          <div className="flex items-center justify-between mb-2">
            <XCircle className="h-5 w-5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Last 5 min</span>
          </div>
          <div className="text-2xl font-bold">
            {errorRate5m ? errorRate5m.metric_value : 0}
          </div>
          <div className="text-sm text-muted-foreground">Auth Errors</div>
        </div>

        <div className="bg-card rounded-lg border p-6">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="h-5 w-5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Last 5 min</span>
          </div>
          <div className="text-2xl font-bold">
            {activeSessions ? activeSessions.metric_value : 0}
          </div>
          <div className="text-sm text-muted-foreground">Active Sessions</div>
        </div>

        <div className="bg-card rounded-lg border p-6">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="h-5 w-5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Last 5 min</span>
          </div>
          <div className="text-2xl font-bold">
            {degradedModeEntries ? degradedModeEntries.metric_value : 0}
          </div>
          <div className="text-sm text-muted-foreground">Degraded Mode Entries</div>
        </div>
      </div>

      {/* Recent Events */}
      <div className="bg-card rounded-lg border p-6">
        <h2 className="text-lg font-semibold mb-4">Recent Auth Events</h2>
        <div className="space-y-2">
          {recentEvents.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No recent events</p>
          ) : (
            recentEvents.map((event) => (
              <div key={event.id} className="flex items-center gap-3 p-3 bg-background rounded-lg">
                {event.event_status === 'success' ? (
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                ) : event.event_status === 'warning' ? (
                  <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{event.event_type.replace(/_/g, ' ')}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(event.created_at).toLocaleString()}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                  event.event_status === 'success' ? 'bg-green-500/10 text-green-600' :
                  event.event_status === 'warning' ? 'bg-yellow-500/10 text-yellow-600' :
                  'bg-red-500/10 text-red-600'
                }`}>
                  {event.event_status}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Recent Errors */}
      {recentErrors.length > 0 && (
        <div className="bg-card rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-4">Recent Errors</h2>
          <div className="space-y-2">
            {recentErrors.map((error) => (
              <div key={error.id} className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-red-600 dark:text-red-400">
                      {error.error_type.replace(/_/g, ' ')}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{error.error_message}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(error.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAuthHealth;
